// ─── Fiche d'un POSTE : récap + liste des opérations ──────────────
// Calque plein écran ouvert depuis la liste des postes. Les opérations sont
// filtrées côté client (la requête, elle, est bornée à l'espace : les règles
// ne savent pas prouver un `where budgetId ==`).
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran, frJMA, useUI, useT, confirmerSuppression,
} from '@mop/core';
import { formatEuro, dateEffective, montantOperation } from './services/calculs';
import { emojiCategorie, cleCategorie, emojiBudget } from './data/categories';
import RecapBudget from './RecapBudget';

export default function EcranBudget({
  budget, operations = [], membres = [], onFermer,
  onNouvelle, onFiche, onEditer, onSupprimer, onRembourser,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [voirExclues, setVoirExclues] = useState(true);

  // Les plus récentes d'abord (date banque > paiement > réception).
  const liste = useMemo(() => {
    const l = operations.filter(o => voirExclues || !o.exclu);
    return l.sort((a, b) => (dateEffective(b) || '').localeCompare(dateEffective(a) || ''));
  }, [operations, voirExclues]);

  const nomPayeur = (o) => o.payeurNom || (membres.find(m => m.uid === o.payeurUid) || {}).nom || '';

  return (
    <GesteRetourEcran actif onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={budget?.nom || ''} iconeTitre={emojiBudget(budget)} onRetour={onFermer}
          action={{ icone: '＋', label: t('budget.depense'), onPress: onNouvelle }} />
        <ScrollView contentContainerStyle={s.corps}>
          <RecapBudget operations={operations} payeurs={budget?.payeurs || []} onRembourser={onRembourser} />

          {liste.length === 0 ? (
            <EmptyState icon="🧾" texte={t('budget.aucuneDepense')} />
          ) : liste.map(o => {
            const remb = o.type === 'remboursement';
            return (
              <LigneSwipe key={o.id} espacement={8} enabled
                onEditer={remb ? undefined : () => onEditer(o)}
                onSupprimer={() => confirmerSuppression(
                  t('commun.supprimer'), o.libelle || formatEuro(o.montant),
                  () => onSupprimer(o).catch(() => {}))}>
                {/* Tap = FICHE, jamais l'édition directe (principe app-wide).
                    C'est aussi d'ici que se lisent les rattachements d'une
                    opération — décision de Cédric le 11/08 : cet écran liste
                    les opérations de l'enveloppe, la fiche dit ce qu'elles
                    honorent. Un remboursement n'a pas de fiche : il est
                    entièrement décrit par sa ligne. */}
                <TouchableOpacity style={s.carte} activeOpacity={remb ? 1 : 0.7}
                  onPress={remb ? undefined : () => (onFiche || onEditer)(o)}>
                  <Text style={{ fontSize: 19 }}>{remb ? '↩️' : emojiCategorie(o.categorie)}</Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={s.libelle} numberOfLines={1}>
                      {remb ? `${o.deNom || '—'} → ${o.aNom || '—'}` : (o.libelle || t(cleCategorie(o.categorie)))}
                    </Text>
                    <Text style={s.sous} numberOfLines={1}>
                      {frJMA(dateEffective(o))}
                      {nomPayeur(o) ? `  ·  ${nomPayeur(o)}` : ''}
                      {o.paye === false ? '  ·  ⏳' : ''}
                      {o.exclu ? '  ·  ⊘' : ''}
                    </Text>
                  </View>
                  <Text style={[s.montant, o.sens === 'revenu' && { color: UI.ok || '#2e7d32' },
                    (o.exclu || o.paye === false) && { opacity: 0.45 }]}>
                    {montantOperation(o.montant, o.sens)}
                  </Text>
                </TouchableOpacity>
              </LigneSwipe>
            );
          })}

          {operations.some(o => o.exclu) && (
            <TouchableOpacity style={s.filtre} activeOpacity={0.7} onPress={() => setVoirExclues(v => !v)}>
              <Text style={s.filtreTxt}>{voirExclues ? '⊘ …' : '⊘ +'}</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12 },
  libelle: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  montant: { fontSize: 15, fontWeight: '800', color: UI.ink },
  filtre: { alignItems: 'center', paddingVertical: 10 },
  filtreTxt: { fontSize: 12.5, color: UI.muted, fontWeight: '700' },
});
